import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import type { Service } from "@/types/service";

export const useServices = () => {
  const { company } = useAuth();
  const queryClient = useQueryClient();

  const { data: services = [], isLoading } = useQuery({
    queryKey: ["services", company?.id],
    queryFn: async () => {
      if (!company?.id) return [];
      
      const { data, error } = await supabase
        .from("services")
        .select("*")
        .eq("company_id", company.id)
        .eq("active", true)
        .order("service_name");

      if (error) throw error;
      return data as Service[];
    },
    enabled: !!company?.id,
  });

  const createService = useMutation({
    mutationFn: async (service: Omit<Service, "id" | "created_at" | "updated_at" | "company_id" | "created_by">) => {
      if (!company?.id) throw new Error("Empresa não encontrada");

      const { data, error } = await supabase
        .from("services")
        .insert({
          ...service,
          company_id: company.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
      toast.success("Serviço cadastrado com sucesso!");
    },
    onError: (error) => {
      console.error("Erro ao criar serviço:", error);
      toast.error("Erro ao cadastrar serviço");
    },
  });

  const updateService = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Service> & { id: string }) => {
      const { data, error } = await supabase
        .from("services")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
      toast.success("Serviço atualizado com sucesso!");
    },
    onError: (error) => {
      console.error("Erro ao atualizar serviço:", error);
      toast.error("Erro ao atualizar serviço");
    },
  });

  const deactivateService = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("services")
        .update({ active: false })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
      toast.success("Serviço inativado com sucesso!");
    },
    onError: (error) => {
      console.error("Erro ao inativar serviço:", error);
      toast.error("Erro ao inativar serviço");
    },
  });

  return {
    services,
    isLoading,
    createService: createService.mutate,
    updateService: updateService.mutate,
    deactivateService: deactivateService.mutate,
    isCreating: createService.isPending,
    isUpdating: updateService.isPending,
  };
};
